import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label } from '../components/ui'

export default function OTPVerification() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const phoneNumber = searchParams.get('phone')
  const inviteToken = searchParams.get('invite')

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  if (!phoneNumber) {
    navigate('/login')
    return null
  }

  // If this is from an invitation, redirect to invitation-specific OTP page
  if (inviteToken) {
    navigate(`/invite/${inviteToken}/verify-otp?phone=${encodeURIComponent(phoneNumber)}&invite=${inviteToken}`)
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/auth/phone/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber,
          otp
        })
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem('auth_token', data.token)
        
        // If there's an invitation, accept it automatically
        if (inviteToken) {
          try {
            const acceptResponse = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/v1/invitations/${inviteToken}/accept`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${data.token}`
                }
              }
            )

            if (acceptResponse.ok) {
              // Successful authentication and invitation acceptance
              // Skip onboarding since user is joining an existing team
              window.location.href = '/dashboard?invitation_accepted=true'
              return
            } else {
              // Authentication succeeded but invitation failed
              console.error('Failed to accept invitation after phone auth')
              // Still skip onboarding if the auth was successful, but show error
              window.location.href = '/dashboard?invite_error=true'
              return
            }
          } catch (inviteErr) {
            console.error('Error accepting invitation after phone auth:', inviteErr)
            // Still skip onboarding if the auth was successful, but show error
            window.location.href = '/dashboard?invite_error=true'
            return
          }
        }
        
        // No invitation - proceed normally
        if (data.needsOnboarding) {
          window.location.href = '/onboarding'
        } else {
          window.location.href = '/dashboard'
        }
      } else {
        alert('Invalid verification code. Please try again.')
      }
    } catch (error) {
      console.error('Verification failed:', error)
      alert('Verification failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setResending(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/v1/auth/phone/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber
        })
      })

      if (response.ok) {
        setCountdown(60)
        alert('Verification code sent!')
      } else {
        alert('Failed to resend code. Please try again.')
      }
    } catch (error) {
      console.error('Resend failed:', error)
      alert('Failed to resend code. Please try again.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-3xl font-bold text-gray-900">ClockInNOut</h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter verification code
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Verify Your Phone</CardTitle>
            <CardDescription>
              We sent a 6-digit code to {phoneNumber}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="otp">Verification Code</Label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg tracking-widest"
                  maxLength={6}
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || otp.length !== 6}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Didn't receive a code?{' '}
                {countdown > 0 ? (
                  <span>Resend in {countdown}s</span>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={resending}
                    className="text-primary-600 hover:text-primary-500 font-medium"
                  >
                    {resending ? 'Sending...' : 'Resend code'}
                  </button>
                )}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="mt-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Back to login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}